/* eslint-disable lingui/text-restrictions */

import { i18n } from "@lingui/core";
import { msg } from "@lingui/macro";

import { isFeatureEnabled } from "@/client/constants/feature-flags";
import { DEFAULT_MAX_TOKENS, DEFAULT_MODEL } from "@/client/constants/llm";
import { axios } from "@/client/libs/axios";
import { useOpenAiStore } from "@/client/stores/openai";
import { useResumeStore } from "@/client/stores/resume";

import { aiCache } from "./cache-manager";
import { openai } from "./client";
import { getOptimalModel } from "./model-selector";
import { optimizePrompt } from "./prompt-optimizer";

const getPrompt = (language = "en") => {
  const languageName = getLanguageName(language);
  return `You are an AI writing assistant specialized in writing copy for resumes.
Do not return anything else except the text you improved. It should not begin with a newline. It should not have any prefix or suffix text.
Improve the writing of the following paragraph and return it in ${languageName}:

Text: """{input}"""

Revised Text: """`;
};

const getLanguageName = (locale: string): string => {
  const languageMap: Record<string, string> = {
    ar: "Arabic",
    en: "English",
    fr: "French",
    es: "Spanish",
    de: "German",
    it: "Italian",
    pt: "Portuguese",
    ru: "Russian",
    ja: "Japanese",
    ko: "Korean",
    zh: "Chinese",
    hi: "Hindi",
    nl: "Dutch",
    sv: "Swedish",
    da: "Danish",
    no: "Norwegian",
    fi: "Finnish",
    pl: "Polish",
    tr: "Turkish",
  };

  const langCode = locale?.split("-")[0] || "en";
  return languageMap[langCode] || "English";
};

export const improveWriting = async (text: string, language?: string) => {
  // Get language from resume store if not provided
  if (!language) {
    try {
      const resumeState = useResumeStore.getState();
      language = resumeState.resume.data.metadata.locale || "en-US";
    } catch {
      language = "en-US";
    }
  }
  const { model, maxTokens, useSystemKey } = useOpenAiStore.getState();

  // If using system API, send request to our backend AI gateway
  if (useSystemKey) {
    try {
      const response = await axios.post("/ai/write/improve", { text, language });
      // Return the text directly as that's what the backend sends
      return response.data || text;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : i18n._(msg`Unknown error`);
      throw new Error(i18n._(msg`Failed to improve writing: ${errorMessage}`));
    }
  }

  // Otherwise use original implementation with direct OpenAI client
  const PROMPT = getPrompt(language);
  let prompt = PROMPT.replace("{input}", text);

  // Get optimal model if feature flag is enabled
  const useOptimizedModels = isFeatureEnabled("OPTIMIZED_AI_MODELS");
  const modelConfig = useOptimizedModels
    ? getOptimalModel("improve")
    : { model: model ?? DEFAULT_MODEL, maxTokens: maxTokens ?? DEFAULT_MAX_TOKENS, temperature: 0 };

  // Check cache first
  const cachedResponse = aiCache.get(prompt, modelConfig.model, "improve");
  if (cachedResponse) {
    return cachedResponse;
  }

  // Optimize prompt to reduce token usage
  const optimizedPrompt = optimizePrompt(prompt, "improve");
  prompt = optimizedPrompt.optimized;

  // Explicitly resolve the openai() promise to fix the typing issue
  const client = await openai();
  const result = await client.chat.completions.create({
    messages: [{ role: "user", content: prompt }],
    model: modelConfig.model,
    max_tokens: modelConfig.maxTokens,
    temperature: modelConfig.temperature,
    stop: ['"""'],
    n: 1,
  });

  if (result.choices.length === 0) {
    throw new Error(i18n._(msg`OpenAI did not return any choices for your text.`));
  }

  const response = result.choices[0].message.content ?? text;

  // Cache the response for future use
  const tokensUsed = result.usage?.total_tokens ?? Math.ceil(prompt.length / 4);
  aiCache.set(prompt, response, modelConfig.model, tokensUsed, "improve");

  return response;
};
